import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Box, Typography, Chip, IconButton } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { IUserProfileState } from 'src/features/profile';
import { CreateSiteAccessModal } from 'src/features/site_access/components/CreateSiteAccessModal';
import {
	ISiteAccess,
	ISiteAccessTableProps,
} from 'src/features/site_access/domain/types';
import { IGetSiteAccessesRequestParams } from 'src/features/site_access/domain/types';
import {
	SITE_ACCESS_LOADING_KEY,
	getSiteAccesses,
} from 'src/features/site_access/store/siteAccessThunks';
import { IUserOption } from 'src/features/user';
import { getGeneralUsers } from 'src/features/user/store/userThunks';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';
import { snakeToTitleCase } from 'src/utils/formatter';

import { SiteAccessTableActions } from './SiteAccessTableActions';

export const SiteAccessTable: React.FC<ISiteAccessTableProps> = ({
	siteSlug,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const { profile } = useSelector(
		(state: RootState) => state.user_profile as IUserProfileState,
	);

	const [queryState, setQueryState] = useState<ITableQueryState>({
		page: 0,
	});
	const [siteAccesses, setSiteAccesses] = useState<ISiteAccess[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [users, setUsers] = useState<IUserOption[]>([]);
	const [clickedRow, setClickedRow] = useState<ISiteAccess>();
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		siteAccess: ISiteAccess,
	) => {
		setClickedRow(siteAccess);
		setAnchorElement(event.currentTarget);
	};

	const columns: IColumn<ISiteAccess>[] = [
		{
			key: 'name',
			label: 'User',
			sortable: true,
			searchable: true,
			render: (siteAccess) =>
				`${siteAccess.first_name} ${siteAccess.last_name}`,
		},
		{
			key: 'user_initials',
			label: "User's Initials",
			render: (siteAccess) => siteAccess.initials || <i>N/A</i>,
		},
		{
			key: 'can_add_patient',
			label: 'Can Add Patient',
			render: (siteAccess) => (siteAccess.can_add_patient ? 'Yes' : 'No'),
		},
		{
			key: 'type',
			label: 'Type',
			filterable: true,
			menuItems: [
				{ label: 'Admin', value: 'admin' },
				{ label: 'Member', value: 'member' },
			],
			render: (siteAccess) => (
				<Chip
					label={snakeToTitleCase(siteAccess.type)}
					variant='outlined'
					size='small'
				/>
			),
		},
		{
			key: 'role',
			label: 'Roles',
			render: (siteAccess) => {
				if (siteAccess.type === 'admin') {
					return <i>N/A</i>;
				}

				return (
					<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
						{siteAccess.roles.map((role: string, idx: number) => (
							<Chip
								key={idx}
								label={snakeToTitleCase(role)}
								variant='outlined'
								size='small'
							/>
						))}
					</Box>
				);
			},
		},
		{
			key: 'status',
			label: 'Status',
			filterable: true,
			menuItems: [
				{ label: 'Active', value: 'active' },
				{ label: 'Suspended', value: 'suspended' },
			],
			render: (siteAccess) => (
				<Chip
					label={snakeToTitleCase(siteAccess.status)}
					variant='outlined'
					size='small'
				/>
			),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (siteAccess) => formatTimestamp(siteAccess.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			render: (siteAccess) =>
				siteAccess.user_id === profile?.id ? null : (
					<IconButton onClick={(e) => handleMenuClick(e, siteAccess)}>
						<MoreVertOutlinedIcon />
					</IconButton>
				),
		},
	];

	const handleFetchSiteAccesses = (
		request: IGetSiteAccessesRequestParams,
		siteSlug: string,
	) => {
		dispatch(
			getSiteAccesses({
				request: request,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					setSiteAccesses(response.results);
					setTotalCount(response.info.count);
				},
			}),
		);
	};

	const handleMenuClose = () => {
		setAnchorElement(null);
		setClickedRow(undefined);
	};

	useEffect(() => {
		const params = buildQueryParams(queryState);
		handleFetchSiteAccesses(params, siteSlug);
	}, [queryState, siteSlug]);

	useEffect(() => {
		dispatch(
			getGeneralUsers({
				onSuccess: (response) => {
					setUsers(response);
				},
			}),
		);
	}, []);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Site Accesses
				</Typography>
				<Box sx={{ width: '231px', height: '36px' }}>
					<PrimaryButton
						onClick={() => setIsCreateModalOpen(true)}
						sx={{
							height: '36px',
							minHeight: '36px',
							'& .MuiButton-root': {
								height: '36px',
								minHeight: '36px',
							},
							backgroundColor: '#BE3327',
							'&:hover': {
								backgroundColor: '#A52D23',
							},
						}}
					>
						Add Site Access
					</PrimaryButton>
				</Box>
			</Box>
			<DataTable
				columns={columns}
				data={siteAccesses}
				loadingKey={SITE_ACCESS_LOADING_KEY}
				emptyMessage='No site accesses found'
				paginationInfo={{ totalCount: totalCount }}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			{clickedRow && (
				<SiteAccessTableActions
					siteSlug={siteSlug}
					selectedRow={clickedRow}
					anchorElement={anchorElement}
					clearAnchorElement={() => setAnchorElement(null)}
					OnActionHit={() => setQueryState({ ...queryState })}
					onClose={handleMenuClose}
				/>
			)}

			<CreateSiteAccessModal
				siteSlug={siteSlug}
				open={isCreateModalOpen}
				onClose={() => setIsCreateModalOpen(false)}
				onSubmit={() => {
					setIsCreateModalOpen(false);
					setQueryState({ page: 0 });
				}}
				users={users}
			/>
		</Box>
	);
};
